/**
 * The user profile card (web-plan.md F3, friends-plan §13): opened from
 * author names and rows, action tiles driven by a FRESH
 * `GET /v1/users/{id}/friendship` — and a placeholder while it flies, never
 * defaults: a card that briefly offers "Add" to a friend teaches distrust.
 *
 * The stealth rules render here: a blocked-by relationship draws NOTHING
 * different (D171/D172), `can_request` arrives pre-computed as if no block
 * existed (D176), an unknown `friend_policy`-shaped relation renders as its
 * raw string. Unfriend and Block confirm; decline is silent.
 */
import { type JSX, Show, createSignal } from "solid-js";

import { currentLifetime, isCurrent } from "../lib/lifetime";
import { AvatarCircle } from "../components/avatar";
import { ActivityIndicator, Icon } from "../components/ds";
import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { Runtime } from "../store/runtime";

export function UserCard(props: { rt: Runtime; t: Dict; onClose(): void }): JSX.Element {
  const rt = props.rt;
  const s = rt.state;
  const [confirming, setConfirming] = createSignal<"unfriend" | "block" | null>(null);

  const userId = () => s.card.userId ?? 0;
  const person = () => s.directory.people[userId()];
  const name = () => mut.nameOf(s.directory, userId());
  const relation = () => s.card.relation;
  const status = () => mut.statusOf(s.presence, userId());
  const isSelf = () => mut.isSelf(s.directory, userId());

  const act = async (action: () => Promise<void>) => {
    const lifetime = currentLifetime();
    setConfirming(null);
    try {
      await action();
      if (!isCurrent(lifetime)) return;
      await rt.reopenCard();
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      rt.showNotice(e instanceof Error ? e.message : String(e));
    }
  };

  // No .echo-transition-enter on this scrim: the card centers by its own inline
  // transform, and the fallback rise keyframe would stomp it. The view
  // transition (swap "sheet") animates the snapshot instead.
  return (
    <div class="echo-scrim" onClick={() => props.onClose()}>
      <div style="display:contents" onClick={(e) => e.stopPropagation()}>
        <div
          class="profile-card profile-card--user echo-material-thick"
          style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:340px;padding:20px;border-radius:20px;display:flex;flex-direction:column;gap:14px"
        >
          <div style="display:flex;align-items:center;gap:12px">
            <span style="position:relative">
              <AvatarCircle
                name={name()}
                createdAt={person()?.createdAt ?? 0}
                size={46}
                fileId={person()?.avatarFileId ?? null}
              />
              <Show when={status()}>
                {(st) => <span class={`presence-dot${dotClass(st())}`} />}
              </Show>
            </span>
            <div style="display:flex;flex-direction:column;min-width:0">
              <span style="font-weight:600">{name()}</span>
              <span style="font-size:12px;color:var(--labels-secondary)">
                <Show when={person()} fallback={`#${userId()}`}>
                  {(p) => `@${p().username}`}
                </Show>
              </span>
              <Show when={relation()?.last_seen_ms != null && status() === null}>
                <span style="font-size:12px;color:var(--labels-tertiary)">
                  {new Date(relation()!.last_seen_ms!).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </Show>
            </div>
          </div>

          <Show when={person()?.bio}>
            <div style="font-size:14px;white-space:pre-wrap">{person()!.bio}</div>
          </Show>

          <Show
            when={relation()}
            fallback={
              // The placeholder while the friendship answer flies (F3): the
              // card must never guess an action row.
              <div style="display:flex;justify-content:center;padding:8px">
                <ActivityIndicator small />
              </div>
            }
          >
            {(rel) => (
              <Show when={!isSelf()}>
                <div class="echo-action-tiles">
                  <Show when={rel().relation === "none"}>
                    <button
                      type="button"
                      class="echo-action-tile"
                      disabled={!rel().can_request}
                      title={rel().can_request ? "" : props.t.cardPolicyClosed()}
                      onClick={() => void act(() => rt.addFriend(userId()))}
                    >
                      <Icon name="person_add" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardAdd()}</span>
                    </button>
                  </Show>
                  <Show when={rel().relation === "outgoing"}>
                    <button
                      type="button"
                      class="echo-action-tile"
                      onClick={() => void act(() => rt.cancelRequest(userId()))}
                    >
                      <Icon name="schedule" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardPending()}</span>
                    </button>
                  </Show>
                  <Show when={rel().relation === "incoming"}>
                    <button
                      type="button"
                      class="echo-action-tile"
                      onClick={() => void act(() => rt.acceptRequest(userId()))}
                    >
                      <Icon name="check" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardAccept()}</span>
                    </button>
                    <button
                      type="button"
                      class="echo-action-tile"
                      onClick={() => void act(() => rt.declineRequest(userId()))}
                    >
                      <Icon name="close" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardDecline()}</span>
                    </button>
                  </Show>
                  <Show when={rel().relation === "friends"}>
                    <button
                      type="button"
                      class="echo-action-tile"
                      onClick={() => {
                        const dm = s.friends.friends.find((f) => f.user_id === userId());
                        if (dm) {
                          rt.openChannel(dm.channel_id);
                          props.onClose();
                        }
                      }}
                    >
                      <Icon name="chat_bubble" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardMessage()}</span>
                    </button>
                    <button
                      type="button"
                      class="echo-action-tile"
                      style="color:var(--accents-red)"
                      onClick={() => setConfirming("unfriend")}
                    >
                      <Icon name="person_remove" size={17} />
                      <span class="echo-action-tile__label">{props.t.cardUnfriend()}</span>
                    </button>
                  </Show>
                  <Show when={!isUnknownRelation(rel().relation)}>
                    <Show
                      when={s.friends.blocked.includes(userId())}
                      fallback={
                        <button
                          type="button"
                          class="echo-action-tile"
                          style="color:var(--accents-red)"
                          onClick={() => setConfirming("block")}
                        >
                          <Icon name="block" size={17} />
                          <span class="echo-action-tile__label">{props.t.cardBlock()}</span>
                        </button>
                      }
                    >
                      <button
                        type="button"
                        class="echo-action-tile"
                        onClick={() => void act(() => rt.unblock(userId()))}
                      >
                        <Icon name="block" size={17} />
                        <span class="echo-action-tile__label">{props.t.cardUnblock()}</span>
                      </button>
                    </Show>
                  </Show>
                </div>
                <Show when={isUnknownRelation(rel().relation)}>
                  {/* An open enum: a relation this build cannot name renders
                      as its raw string, and the card offers nothing. */}
                  <div style="text-align:center;font-size:13px;color:var(--labels-secondary)">
                    {String(rel().relation)}
                  </div>
                </Show>
              </Show>
            )}
          </Show>

          <Show when={confirming()}>
            {(what) => (
              <div style="display:flex;gap:8px;justify-content:center">
                <button
                  type="button"
                  class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
                  onClick={() =>
                    void act(() =>
                      what() === "unfriend" ? rt.unfriendUser(userId()) : rt.blockUser(userId()),
                    )
                  }
            >
              <Icon name={what() === "unfriend" ? "close" : "highlight_off"} size={15} />
              {what() === "unfriend" ? props.t.cardUnfriend() : props.t.cardBlock()}?
                </button>
                <button
                  type="button"
                  class="echo-button echo-button--medium echo-button--secondary"
                  onClick={() => setConfirming(null)}
            >
              <Icon name="close" size={15} />
              {props.t.consoleCancel()}
                </button>
              </div>
            )}
          </Show>
        </div>
      </div>
    </div>
  );
}

function isUnknownRelation(rel: string): boolean {
  return !["none", "outgoing", "incoming", "friends", "self"].includes(rel);
}

function dotClass(status: string): string {
  switch (status) {
    case "afk":
      return " presence-dot--afk";
    case "dnd":
      return " presence-dot--dnd";
    case "online":
    case "invisible":
      return "";
    default:
      return " presence-dot--off";
  }
}
